import {
  DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined,
} from '@ant-design/icons';
import {
  Card, Checkbox, Col, DatePicker, Empty, Popconfirm, Progress, Row, Spin, Tag, Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';

const { RangePicker } = DatePicker;

export const MODULOS = [
  { value: 'entidad', label: 'Empleados' },
  { value: 'nominas', label: 'Nómina' },
  { value: 'relojchecador', label: 'Asistencia (Reloj Checador)' },
  { value: 'plazas', label: 'Plazas: Movimientos y Costos' },
  { value: 'operacion_nomina', label: 'Operación de Nómina' },
];

export const TIPOS_GRAFICA = [
  { value: 'barra', label: 'Barras' },
  { value: 'barra_horizontal', label: 'Barras horizontales' },
  { value: 'linea', label: 'Línea' },
  { value: 'pastel', label: 'Pastel' },
];

export const TIPOS_DE_UNA_SOLA_METRICA = ['linea', 'pastel'];

export const AGREGACIONES_AGRUPABLES_EN_OTROS = ['SUM', 'COUNT'];

export const ETIQUETAS_AGREGACION = {
  SUM: 'Suma',
  AVG: 'Promedio',
  COUNT: 'Conteo',
  MIN: 'Mínimo',
  MAX: 'Máximo',
  DISTINCT: 'Contar distintos',
};

export const AGRUPACIONES_FECHA = [
  { value: 'dia', label: 'Por día' },
  { value: 'mes', label: 'Por mes' },
  { value: 'anio', label: 'Por año' },
];

export const COLOR_BARRA = '#3496db';
export const PALETA = ['#3496db', '#66bc1b', '#f3c623', '#e35767', '#8e44ad', '#14b1ab', '#ff8c42'];

export const formatearNumero = (valor) => Number(valor || 0).toLocaleString('es-MX', {
  maximumFractionDigits: 2,
});

const TRANSICION = 'all 0.15s ease';

// Con muchas categorías (p. ej. 108 meses) las barras no deben salirse de la
// tarjeta: cada una tiene un ancho mínimo y, si no caben, la gráfica hace scroll
// horizontal dentro de su propio contenedor (no depende de que quien la use
// ponga overflow, como hace el Dashboard y no el Constructor).
const ANCHO_MINIMO_BARRA = 56;
const ESTILO_SCROLL_HORIZONTAL = { overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4 };
const ESTILO_ETIQUETA_BARRA = {
  fontSize: 12,
  marginTop: 6,
  textAlign: 'center',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Eje Y de las gráficas con altura (barras verticales, agrupadas y línea).
const ALTO_AREA = 180;
const MARGEN_SUPERIOR = 18; // espacio para el valor encima de la barra más alta
const ANCHO_EJE_Y = 64;
const MAXIMO_BARRAS_CON_VALOR = 20; // con más, los valores se encimarían
const COLOR_EJE = '#d9d9d9';
const COLOR_TEXTO_EJE = 'rgba(0, 0, 0, 0.45)';

export const formatearCorto = (valor) => Number(valor || 0).toLocaleString('es-MX', {
  notation: 'compact', maximumFractionDigits: Math.abs(Number(valor || 0)) < 10 ? 2 : 1,
});

// Marcas "redondas" (pasos de 1, 2, 2.5 o 5 × 10^n) cuyo tope cubre el valor máximo.
const calcularEjeY = (maxValor, divisiones = 4) => {
  const maximo = Math.max(Number(maxValor) || 0, 0);
  if (maximo === 0) return { tope: 1, marcas: [0, 1] };
  const pasoBruto = maximo / divisiones;
  const potencia = 10 ** Math.floor(Math.log10(pasoBruto));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * potencia).find((p) => p >= pasoBruto);
  const tope = Math.ceil(maximo / paso) * paso;
  const marcas = [];
  for (let i = 0; i * paso <= tope + paso / 2; i += 1) {
    marcas.push(Number((i * paso).toPrecision(12)));
  }
  return { tope, marcas };
};

const alturaDe = (valor, tope) => Math.max(0, (valor / (tope || 1)) * ALTO_AREA);

const EjeY = ({ marcas, tope }) => (
  <div style={{
    position: 'relative',
    flex: `0 0 ${ANCHO_EJE_Y}px`,
    height: ALTO_AREA + MARGEN_SUPERIOR,
    borderRight: `1px solid ${COLOR_EJE}`,
  }}
  >
    {marcas.map((marca) => (
      <span
        key={marca}
        style={{
          position: 'absolute',
          right: 6,
          bottom: alturaDe(marca, tope),
          transform: 'translateY(50%)',
          fontSize: 11,
          color: COLOR_TEXTO_EJE,
          whiteSpace: 'nowrap',
        }}
      >
        {formatearCorto(marca)}
      </span>
    ))}
  </div>
);
EjeY.propTypes = {
  marcas: PropTypes.arrayOf(PropTypes.number).isRequired,
  tope: PropTypes.number.isRequired,
};

// Líneas guía horizontales detrás de las barras, a la altura de cada marca.
const LineasGuia = ({ marcas, tope }) => (
  <div style={{
    position: 'absolute', left: 0, right: 0, top: MARGEN_SUPERIOR, height: ALTO_AREA, pointerEvents: 'none',
  }}
  >
    {marcas.map((marca) => (
      <div
        key={marca}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: alturaDe(marca, tope),
          borderTop: marca === 0 ? `1px solid ${COLOR_EJE}` : '1px dashed #f0f0f0',
        }}
      />
    ))}
  </div>
);
LineasGuia.propTypes = EjeY.propTypes;

const ESTILO_VALOR_SOBRE_BARRA = {
  fontSize: 10, color: COLOR_TEXTO_EJE, whiteSpace: 'nowrap', marginBottom: 2,
};

const datoValorShape = PropTypes.shape({
  nombre: PropTypes.string,
  valor: PropTypes.number,
});

const datoShape = PropTypes.shape({
  etiqueta: PropTypes.string,
  valores: PropTypes.arrayOf(datoValorShape),
});

const BarrasVerticales = ({ datos, maxValor }) => {
  const [activo, setActivo] = useState(null);
  const { tope, marcas } = calcularEjeY(maxValor);
  const mostrarValores = datos.length <= MAXIMO_BARRAS_CON_VALOR;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <EjeY marcas={marcas} tope={tope} />
      <div style={{ ...ESTILO_SCROLL_HORIZONTAL, flex: 1, minWidth: 0 }}>
        <div style={{
          position: 'relative', display: 'flex', gap: 12, padding: '0 8px', minWidth: 'max-content',
        }}
        >
          <LineasGuia marcas={marcas} tope={tope} />
          {datos.map((d, i) => (
            <Tooltip key={d.etiqueta} title={`${d.etiqueta}: ${formatearNumero(d.valor)}`}>
              <div
                onMouseEnter={() => setActivo(i)}
                onMouseLeave={() => setActivo(null)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: `1 0 ${ANCHO_MINIMO_BARRA}px`,
                  width: ANCHO_MINIMO_BARRA,
                  cursor: 'default',
                  position: 'relative',
                }}
              >
                <div style={{
                  height: ALTO_AREA + MARGEN_SUPERIOR,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                >
                  {mostrarValores && (
                    <span style={ESTILO_VALOR_SOBRE_BARRA}>{formatearCorto(d.valor)}</span>
                  )}
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 48,
                      height: Math.max(2, alturaDe(d.valor, tope)),
                      background: COLOR_BARRA,
                      borderRadius: '4px 4px 0 0',
                      transition: TRANSICION,
                      filter: activo === i ? 'brightness(1.15)' : 'none',
                      transform: activo === i ? 'scaleX(1.08)' : 'none',
                    }}
                  />
                </div>
                <span style={{ ...ESTILO_ETIQUETA_BARRA, fontWeight: activo === i ? 600 : 400 }}>
                  {d.etiqueta}
                </span>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};
BarrasVerticales.propTypes = {
  datos: PropTypes.arrayOf(datoShape).isRequired,
  maxValor: PropTypes.number.isRequired,
};

const BarrasHorizontales = ({ datos, maxValor }) => {
  const [activo, setActivo] = useState(null);
  return (
    <div>
      {datos.map((d, i) => (
        <Tooltip key={d.etiqueta} title={`${d.etiqueta}: ${formatearNumero(d.valor)}`}>
          <Row
            align="middle"
            gutter={8}
            onMouseEnter={() => setActivo(i)}
            onMouseLeave={() => setActivo(null)}
            style={{
              marginBottom: 10,
              transition: TRANSICION,
              opacity: activo === null || activo === i ? 1 : 0.6,
            }}
          >
            <Col flex="120px" style={{ fontSize: 12, fontWeight: activo === i ? 600 : 400 }}>{d.etiqueta}</Col>
            <Col flex="auto">
              <Progress
                percent={Math.round((d.valor / (maxValor || 1)) * 100)}
                format={() => formatearNumero(d.valor)}
                strokeColor={COLOR_BARRA}
              />
            </Col>
          </Row>
        </Tooltip>
      ))}
    </div>
  );
};
BarrasHorizontales.propTypes = {
  datos: PropTypes.arrayOf(datoShape).isRequired,
  maxValor: PropTypes.number.isRequired,
};

const GraficaLinea = ({ datos, maxValor }) => {
  const [activo, setActivo] = useState(null);
  const { tope, marcas } = calcularEjeY(maxValor);
  const alto = ALTO_AREA;
  const margenLateral = 30; // que el primer y el último punto (y su etiqueta) no se corten
  const ancho = Math.max(300, datos.length * 90);
  const paso = datos.length > 1 ? (ancho - 2 * margenLateral) / (datos.length - 1) : 0;
  const puntos = datos.map((d, i) => ({
    x: datos.length > 1 ? margenLateral + i * paso : ancho / 2,
    y: MARGEN_SUPERIOR + alto - alturaDe(d.valor, tope),
    d,
  }));
  const puntosStr = puntos.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <EjeY marcas={marcas} tope={tope} />
      <div style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}>
        <svg width={ancho} height={MARGEN_SUPERIOR + alto + 30}>
          {marcas.map((marca) => (
            <line
              key={marca}
              x1={0}
              x2={ancho}
              y1={MARGEN_SUPERIOR + alto - alturaDe(marca, tope)}
              y2={MARGEN_SUPERIOR + alto - alturaDe(marca, tope)}
              stroke={marca === 0 ? COLOR_EJE : '#f0f0f0'}
              strokeDasharray={marca === 0 ? undefined : '4 3'}
            />
          ))}
          <polyline points={puntosStr} fill="none" stroke={COLOR_BARRA} strokeWidth={2} />
          {puntos.map((p, i) => (
            <Tooltip key={p.d.etiqueta} title={`${p.d.etiqueta}: ${formatearNumero(p.d.valor)}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r={activo === i ? 7 : 4}
                fill={COLOR_BARRA}
                style={{ transition: TRANSICION, cursor: 'default' }}
                onMouseEnter={() => setActivo(i)}
                onMouseLeave={() => setActivo(null)}
              />
            </Tooltip>
          ))}
          {datos.length <= MAXIMO_BARRAS_CON_VALOR && puntos.map((p) => (
            <text key={`v-${p.d.etiqueta}`} x={p.x} y={p.y - 9} fontSize={10} fill={COLOR_TEXTO_EJE} textAnchor="middle">
              {formatearCorto(p.d.valor)}
            </text>
          ))}
          {puntos.map((p) => (
            <text key={p.d.etiqueta} x={p.x} y={MARGEN_SUPERIOR + alto + 20} fontSize={11} textAnchor="middle">
              {p.d.etiqueta}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};
GraficaLinea.propTypes = {
  datos: PropTypes.arrayOf(datoShape).isRequired,
  maxValor: PropTypes.number.isRequired,
};

const puntoEnCirculo = (cx, cy, r, anguloGrados) => {
  const rad = ((anguloGrados - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const pathDeSector = (cx, cy, r, anguloInicial, anguloFinal) => {
  const inicio = puntoEnCirculo(cx, cy, r, anguloFinal);
  const fin = puntoEnCirculo(cx, cy, r, anguloInicial);
  const arcoGrande = anguloFinal - anguloInicial > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${inicio.x} ${inicio.y} A ${r} ${r} 0 ${arcoGrande} 0 ${fin.x} ${fin.y} Z`;
};

const GraficaPastel = ({ datos }) => {
  const [activo, setActivo] = useState(null);
  const total = datos.reduce((acc, d) => acc + d.valor, 0) || 1;
  const cx = 100;
  const cy = 100;
  const r = 90;
  let anguloAcumulado = 0;

  return (
    <Row align="middle" gutter={16}>
      <Col>
        <svg width={200} height={200}>
          {datos.length === 1 ? (
            <circle cx={cx} cy={cy} r={r} fill={PALETA[0]} />
          ) : datos.map((d, i) => {
            const anguloInicial = anguloAcumulado;
            const anguloFinal = anguloAcumulado + (d.valor / total) * 360;
            anguloAcumulado = anguloFinal;
            return (
              <Tooltip key={d.etiqueta} title={`${d.etiqueta}: ${formatearNumero(d.valor)}`}>
                <path
                  d={pathDeSector(cx, cy, activo === i ? r + 4 : r, anguloInicial, anguloFinal)}
                  fill={PALETA[i % PALETA.length]}
                  style={{ transition: TRANSICION, cursor: 'default' }}
                  onMouseEnter={() => setActivo(i)}
                  onMouseLeave={() => setActivo(null)}
                />
              </Tooltip>
            );
          })}
        </svg>
      </Col>
      <Col flex="auto">
        {datos.map((d, i) => (
          <div
            key={d.etiqueta}
            onMouseEnter={() => setActivo(i)}
            onMouseLeave={() => setActivo(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
              cursor: 'default',
              fontWeight: activo === i ? 600 : 400,
              transition: TRANSICION,
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%', background: PALETA[i % PALETA.length], display: 'inline-block',
            }}
            />
            <span style={{ fontSize: 12 }}>
              {d.etiqueta}
              {' '}
              (
              {formatearNumero(d.valor)}
              )
            </span>
          </div>
        ))}
      </Col>
    </Row>
  );
};
GraficaPastel.propTypes = { datos: PropTypes.arrayOf(datoShape).isRequired };

const BarrasAgrupadas = ({ datos, series, maxValor }) => {
  const [activo, setActivo] = useState(null);
  const { tope, marcas } = calcularEjeY(maxValor);
  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 8 }}>
        {series.map((nombre, i) => (
          <Col
            key={nombre}
            style={{
              fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%', background: PALETA[i % PALETA.length], display: 'inline-block',
            }}
            />
            {nombre}
          </Col>
        ))}
      </Row>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <EjeY marcas={marcas} tope={tope} />
        <div style={{ ...ESTILO_SCROLL_HORIZONTAL, flex: 1, minWidth: 0 }}>
          <div style={{
            position: 'relative', display: 'flex', gap: 16, padding: '0 8px', minWidth: 'max-content',
          }}
          >
            <LineasGuia marcas={marcas} tope={tope} />
            {datos.map((d, di) => (
              <div
                key={d.etiqueta}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  // Cada grupo mide al menos lo que ocupan sus barras (16px + 3px cada una).
                  flex: `1 0 ${Math.max(ANCHO_MINIMO_BARRA, series.length * 19)}px`,
                  width: Math.max(ANCHO_MINIMO_BARRA, series.length * 19),
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'flex-end', gap: 3, height: ALTO_AREA + MARGEN_SUPERIOR,
                }}
                >
                  {d.valores.map((v, vi) => {
                    const key = `${di}-${vi}`;
                    return (
                      <Tooltip key={v.nombre} title={`${v.nombre}: ${formatearNumero(v.valor)}`}>
                        <div
                          onMouseEnter={() => setActivo(key)}
                          onMouseLeave={() => setActivo(null)}
                          style={{
                            width: 16,
                            height: Math.max(2, alturaDe(v.valor, tope)),
                            background: PALETA[vi % PALETA.length],
                            borderRadius: '3px 3px 0 0',
                            transition: TRANSICION,
                            filter: activo === key ? 'brightness(1.15)' : 'none',
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </div>
                <Tooltip title={d.etiqueta}>
                  <span style={ESTILO_ETIQUETA_BARRA}>{d.etiqueta}</span>
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
BarrasAgrupadas.propTypes = {
  datos: PropTypes.arrayOf(datoShape).isRequired,
  series: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxValor: PropTypes.number.isRequired,
};

const GraficaCard = ({
  grafica, esAdmin, onEditar, onCambiarVisibilidad, onDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState([]);
  const [rangoFechas, setRangoFechas] = useState(null);
  const [historicoCompleto, setHistoricoCompleto] = useState(false);
  const [periodoAplicado, setPeriodoAplicado] = useState(null);

  const tieneFiltroFecha = !!grafica.campo_fecha_filtro_detalle;

  const fetchDatos = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (tieneFiltroFecha) {
        if (historicoCompleto) {
          params.historico_completo = 1;
        } else if (rangoFechas) {
          [params.fecha_inicio, params.fecha_fin] = rangoFechas.map((m) => m.format('YYYY-MM-DD'));
        }
      }
      const res = await API.get(`configuraciones/configuraciones-graficas/${grafica.id}/datos/`, { params });
      setDatos(res?.data?.datos || []);
      setPeriodoAplicado(res?.data?.periodo_aplicado || null);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  }, [grafica.id, rangoFechas, historicoCompleto, tieneFiltroFecha]);

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchDatos();
    return () => { mounted = false; };
  }, [fetchDatos]);

  const onEliminar = async () => {
    try {
      await API.delete(`configuraciones/configuraciones-graficas/${grafica.id}/`);
      onSuccess({}, 'Gráfica eliminada correctamente');
      if (onDeleted) onDeleted(grafica.id);
    } catch (err) {
      onError(err);
    }
  };

  const esMultiMetrica = (grafica.metricas || []).length > 1;
  const nombresDeSeries = (grafica.metricas || []).map((m) => m.metrica_detalle?.etiqueta_visible);
  const datosAplanados = datos.map((d) => ({
    etiqueta: d.etiqueta,
    valor: d.valores?.[0]?.valor || 0,
  }));
  const maxValor = esMultiMetrica
    ? Math.max(1, ...datos.flatMap((d) => d.valores.map((v) => v.valor)))
    : Math.max(1, ...datosAplanados.map((d) => d.valor));

  // El usuario que ve la gráfica puede preferir un tipo distinto al que
  // definió el administrador (ver GraficaOcultaPorUsuario) — es puramente
  // visual para ese usuario. No aplica si hay varias métricas: ahí siempre
  // se agrupa en barras (BarrasAgrupadas), sin importar el tipo elegido.
  const tipoEfectivo = grafica.tipo_grafica_preferido || grafica.tipo_grafica;

  const renderGrafica = () => {
    if (!datos.length) return <Empty description="Sin datos para mostrar" />;
    if (esMultiMetrica) {
      return <BarrasAgrupadas datos={datos} series={nombresDeSeries} maxValor={maxValor} />;
    }
    if (tipoEfectivo === 'linea') return <GraficaLinea datos={datosAplanados} maxValor={maxValor} />;
    if (tipoEfectivo === 'pastel') return <GraficaPastel datos={datosAplanados} />;
    if (tipoEfectivo === 'barra') return <BarrasVerticales datos={datosAplanados} maxValor={maxValor} />;
    return <BarrasHorizontales datos={datosAplanados} maxValor={maxValor} />;
  };

  return (
    <Card
      title={(
        <>
          {grafica.nombre}
          {grafica.visible_en_dashboard === false && (
            <Tag color="orange" style={{ marginLeft: 8 }}>Oculta del Dashboard</Tag>
          )}
        </>
      )}
      size="small"
      style={grafica.visible_en_dashboard === false ? { opacity: 0.75 } : undefined}
      extra={esAdmin && (
        <>
          {onCambiarVisibilidad && (
            <Tooltip
              title={grafica.visible_en_dashboard === false
                ? 'Oculta: nadie la ve en el Dashboard. Clic para mostrarla'
                : 'Visible en el Dashboard. Clic para ocultarla'}
            >
              {grafica.visible_en_dashboard === false ? (
                <EyeInvisibleOutlined
                  style={{ marginRight: 12 }}
                  onClick={() => onCambiarVisibilidad(grafica, true)}
                />
              ) : (
                <EyeOutlined
                  style={{ marginRight: 12 }}
                  onClick={() => onCambiarVisibilidad(grafica, false)}
                />
              )}
            </Tooltip>
          )}
          {onEditar && (
            <EditOutlined style={{ marginRight: 12 }} onClick={() => onEditar(grafica)} />
          )}
          <Popconfirm title="¿Eliminar esta gráfica?" onConfirm={onEliminar}>
            <DeleteOutlined />
          </Popconfirm>
        </>
      )}
    >
      <Spin spinning={loading}>
        {tieneFiltroFecha && (
          <Row align="middle" gutter={12} style={{ marginBottom: 8 }}>
            <Col>
              <RangePicker
                size="small"
                format="DD/MM/YYYY"
                disabled={historicoCompleto}
                onChange={(valores) => setRangoFechas(valores)}
              />
            </Col>
            <Col>
              <Checkbox
                checked={historicoCompleto}
                onChange={(e) => setHistoricoCompleto(e.target.checked)}
              >
                Ver histórico completo
              </Checkbox>
            </Col>
          </Row>
        )}
        {periodoAplicado && !historicoCompleto && !rangoFechas && (
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>
            Mostrando: Nómina
            {' '}
            {periodoAplicado.numero_de_nomina}
            {' '}
            (
            {periodoAplicado.fecha_inicial}
            {' '}
            –
            {' '}
            {periodoAplicado.fecha_final}
            )
          </div>
        )}
        {renderGrafica()}
      </Spin>
    </Card>
  );
};

GraficaCard.propTypes = {
  grafica: PropTypes.shape({
    id: PropTypes.number,
    nombre: PropTypes.string,
    tipo_grafica: PropTypes.string,
    tipo_grafica_preferido: PropTypes.string,
    limite: PropTypes.number,
    agrupar_resto_en_otros: PropTypes.bool,
    modulo: PropTypes.string,
    metricas: PropTypes.arrayOf(PropTypes.shape({
      metrica_detalle: PropTypes.shape({ etiqueta_visible: PropTypes.string }),
    })),
    campo_fecha_filtro_detalle: PropTypes.shape({}),
    visible_en_dashboard: PropTypes.bool,
  }).isRequired,
  esAdmin: PropTypes.bool,
  onEditar: PropTypes.func,
  onCambiarVisibilidad: PropTypes.func,
  onDeleted: PropTypes.func,
};

GraficaCard.defaultProps = {
  esAdmin: false,
  onEditar: null,
  onCambiarVisibilidad: null,
  onDeleted: null,
};

export default GraficaCard;
